import { useState, useEffect } from "react";
import {
  Network,
  Boxes,
  TrendingUp,
  Smartphone,
  Headphones,
  Watch,
  Home,
  Laptop,
  Gamepad2,
} from "lucide-react";
import { categories, globeImage, networkFeatures } from "../mock";

const icons = { Network, Boxes, TrendingUp, Smartphone, Headphones, Watch, Home, Laptop, Gamepad2 };

const useTypewriter = (words, typeSpeed = 70, pause = 1600) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[index];
    let timeout;
    if (!deleting && text.length < current.length) {
      timeout = setTimeout(() => setText(current.slice(0, text.length + 1)), typeSpeed);
    } else if (!deleting && text.length === current.length) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && text.length > 0) {
      timeout = setTimeout(() => setText(current.slice(0, text.length - 1)), 30);
    } else if (deleting && text.length === 0) {
      setDeleting(false);
      setIndex((index + 1) % words.length);
    }
    return () => clearTimeout(timeout);
  }, [text, deleting, index, words, typeSpeed, pause]);

  return { text, activeIndex: index };
};

const chipPositions = [
  { top: "6%", left: "4%", animationDelay: "0s" },
  { top: "22%", right: "2%", animationDelay: "0.8s" },
  { top: "46%", left: "0%", animationDelay: "1.6s" },
  { top: "62%", right: "6%", animationDelay: "2.4s" },
  { top: "80%", left: "12%", animationDelay: "3.2s" },
  { top: "40%", right: "22%", animationDelay: "4s" },
];

const dotPositions = [
  { top: "30%", left: "38%" },
  { top: "55%", left: "52%" },
  { top: "42%", left: "64%" },
  { top: "68%", left: "34%" },
];

const NetworkSection = ({ onSignup }) => {
  const { text, activeIndex } = useTypewriter(categories.map((c) => c.name));

  return (
    <section className="ws-network" id="wat-we-doen">
      <div className="ws-network-inner">
        <h2>Toegang tot ons wereldwijde netwerk. Eén partner</h2>
        <p className="lead">
          Krijg toegang tot een groot netwerk van geautoriseerde distributeurs en
          fabrikanten via WS Global Trading. Wij zorgen voor kwaliteit, logistiek
          en betalingsvoorwaarden. Simpel, betrouwbaar, efficiënt.
        </p>

        <div className="ws-search-card">
          <div className="ws-search-input">
            <span className="placeholder-label">Ik ben op zoek naar:</span>
            <span className="typed-text">{text}</span>
            <span className="typed-cursor" />
          </div>
          <div className="ws-search-results">
            {categories.map((cat, i) => {
              const Icon = icons[cat.icon];
              return (
                <div key={cat.name} className={`ws-search-result ${i === activeIndex ? "active" : ""}`}>
                  <div className="ws-search-result-icon">
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className="r-name">{cat.name}</div>
                    <div className="r-ean">{cat.tagline}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="ws-btn ws-search-cta" onClick={onSignup}>
            Vraag account aan
          </button>
        </div>

        <div className="ws-globe-wrap">
          <img src={globeImage} alt="Wereldbol" className="ws-globe-img" loading="lazy" />
          {categories.map((cat, i) => {
            const Icon = icons[cat.icon];
            return (
              <div key={cat.name} className="globe-chip" style={chipPositions[i]}>
                <div className="globe-chip-icon">
                  <Icon size={18} />
                </div>
                <div className="c-name">{cat.name}</div>
              </div>
            );
          })}
          {dotPositions.map((pos, i) => (
            <span key={i} className="globe-dot" style={{ ...pos, animationDelay: `${i * 0.6}s` }} />
          ))}
        </div>

        <div className="ws-features">
          {networkFeatures.map((feature) => {
            const Icon = icons[feature.icon];
            return (
              <div className="ws-feature" key={feature.title}>
                <div className="f-icon">
                  <Icon size={22} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default NetworkSection;
